import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as Icon from 'react-feather'
import validator from 'validator'
import tippy from 'tippy.js'
import { toArray } from 'lodash'

import './QuickSearch.scss'
import QuickSearchResult from '../QuickSearchResult/QuickSearchResult'
import toastCenter from '../../shared/toastCenter'
import { OrderListAPI } from '../../api/DesignOrder/OrderList'
import { urlSearchParams } from '../../containers/DesignOrderBoard/utils/helper'
import { isSearchable, MIN_SEARCH_LENGTH } from '../../shared/constant'
import Spinner from '../Loader/Spinner/Spinner'

let quicksearchTimer

const QuickSearch = () => {
  const [isActive, setIsActive] = useState(false)
  const [isShowResult, setIsShowResult] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchResult, setSearchResult] = useState([])

  const inputRef = useRef()

  const minLengthTip = useRef(null)

  useEffect(() => {
    minLengthTip.current = tippy(inputRef.current, {
      content: `Type at least ${MIN_SEARCH_LENGTH} characters to search`,
      animation: 'shift-away',
      placement: 'bottom',
      trigger: 'manual',
    })

    return () => minLengthTip.current.destroy()
  }, [])

  // NGS-1780: 1-2 characters never reach the API, so say why rather than
  // leaving the box looking like it searched and found nothing
  const toggleMinLengthTip = useCallback((value) => {
    if (!validator.isEmpty(value) && !isSearchable(value)) {
      minLengthTip.current.show()
    } else {
      minLengthTip.current.hide()
    }
  }, [])

  const keydownHandler = useCallback((e) => {
    if (e.keyCode === 81 && e.ctrlKey) {
      inputRef.current.focus()
    }

    if (e.keyCode === 27) {
      inputRef.current.blur()
      setSearchResult([])
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', keydownHandler)

    return () => {
      document.removeEventListener('keydown', keydownHandler)
    }
  })

  const handleClick = useCallback(() => {
    if (validator.isEmpty(searchValue)) {
      setIsActive(true)
      inputRef.current.focus()
    } else {
      setSearchResult([])
      setIsShowResult(false)
      setSearchValue('')
      minLengthTip.current.hide()
    }
  }, [searchValue])

  const handleFocus = useCallback(() => {
    setIsActive(true)
    if (searchResult.length > 0) {
      setIsShowResult(true)
    } else {
      setIsShowResult(false)
    }
  }, [searchResult.length])

  const handleBlur = useCallback(() => {
    setIsActive(false)
    setIsShowResult(false)
    minLengthTip.current.hide()
  }, [])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      e.preventDefault()
      return false
    }
  }, [])

  const handleHideResult = useCallback(() => {
    setIsShowResult(false)
  }, [])

  const getSearchResultByKeyword = useCallback((keyword) => {
    const orderListApi = new OrderListAPI()
    const onNext = (response) => {
      if (response.success) {
        const orderList = toArray(response.data.orders)
        setSearchResult(orderList)
      }
    }
    const onComplete = () => {
      setIsSearching(false)
    }
    const onError = () => {
      setIsSearching(false)
      toastCenter.messageServerError()
    }

    // we need speed up searching with limit 10
    const queryString = urlSearchParams({ customSearch: keyword, limit: 10 })
    orderListApi.subscribe(queryString, onNext, onComplete, onError)
  }, [])

  const handleChange = useCallback(
    (e) => {
      e.persist()

      setSearchValue(e.target.value)
      setIsSearching(true)
      toggleMinLengthTip(e.target.value)

      if (quicksearchTimer !== undefined) {
        clearTimeout(quicksearchTimer)
      }

      quicksearchTimer = setTimeout(function () {
        // NGS-1780: a short term is stripped from the query string, so firing
        // anyway would render the unfiltered order list as search results
        if (isSearchable(e.target.value)) {
          getSearchResultByKeyword(e.target.value)
          setIsShowResult(true)
        } else {
          setSearchResult([])
          setIsShowResult(false)
          setIsSearching(false)
        }
      }, 500)
    },
    [getSearchResultByKeyword, toggleMinLengthTip]
  )

  return (
    <div className="position-relative quick-search-wrapp">
      <div
        className={`quick-search-input-wrapp ${
          isActive || !validator.isEmpty(searchValue) ? 'active' : ''
        }`}
      >
        <form>
          <input
            type="text"
            ref={inputRef}
            className=""
            value={searchValue}
            placeholder="Quick Search (CTRL+Q)"
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="btn btn-icon-search"
            onClick={handleClick}
          >
            {validator.isEmpty(searchValue) ? (
              <Icon.Search className="feather" />
            ) : (
              <Icon.X className="feather" />
            )}
          </button>
        </form>
      </div>
      {isSearching && <Spinner />}
      <QuickSearchResult
        results={searchResult}
        showResult={isShowResult}
        onHideResult={handleHideResult}
      />
    </div>
  )
}

export default React.memo(QuickSearch)
